import dayjs from 'dayjs'
import { Dispatch, SetStateAction } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import { t } from '@lingui/macro'

import { defaultLpsInfos, getInsIdentityResultMessage, INS_OID } from '@/constants/dmp-connect'
import { useLazyGetInsFromIdentityInformationQuery } from '@/core/services/dmp-connect-js2'
import { useGetPatientQuery, useUpdatePatientMutation } from '@/core/services/patient'
import { PatientProfileFormData } from '@/features/patient/patient-profile-form'
import { appendAlert } from '@/redux/reducers/alerts.reducer'
import { selectDMPConnectTlsiConnectorId } from '@/redux/reducers/dmp-connect.reducer'
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { patientGenderToInsSex } from '@/utils/patient'

import { INFO_PATIENT_PAGE_ALERTS } from './constants'

type UseSearchInsProps = {
  patientId: string
  setFormErrors: Dispatch<SetStateAction<FieldErrors<PatientProfileFormData>>>
}

export function useSearchIns({ patientId, setFormErrors }: UseSearchInsProps) {
  const { enqueueSnackbar } = useSnackbar()

  const dispatch = useAppDispatch()
  const tlsiConnectorId = useAppSelector(selectDMPConnectTlsiConnectorId)

  const { data: patient } = useGetPatientQuery(patientId)
  const [getInsFromIdentityInformation, { isFetching: isFetchingIns }] = useLazyGetInsFromIdentityInformationQuery()
  const [updatePatient] = useUpdatePatientMutation()

  const searchIns = async () => {
    if (!patient || !patient.userInfo.birthday || !tlsiConnectorId) return
    try {
      const LpsInfos = { ...defaultLpsInfos, s_instance: tlsiConnectorId }
      const s_given = patient.userInfo.name
      const s_birthName = patient.userInfo.birthSurname || patient.userInfo.surname

      const regex = /^[a-zA-Z\-\' ]*$/
      const formErrors: FieldErrors<PatientProfileFormData> = {}

      if (!regex.test(s_birthName)) {
        const field = patient.userInfo.birthSurname ? 'birthSurname' : 'surname'
        formErrors[field] = {
          message: t({
            id: 'Caratères autorisés: tiret (-), apostrophe (*), espace ( )',
            message: "Allowed characters: hyphen (-), apostrophe ('), space ( )",
          }),
          type: 'validate',
        }
      }
      if (!regex.test(s_given)) {
        formErrors.name = {
          message: t({
            id: 'Caratères autorisés: tiret (-), apostrophe (*), espace ( )',
            message: "Allowed characters: hyphen (-), apostrophe ('), space ( )",
          }),
          type: 'validate',
        }
      }
      if (Object.keys(formErrors).length) {
        setFormErrors(formErrors)
        return
      }

      const givenList = s_given.split(' ')
      if (givenList.length > 1) givenList.push(s_given)

      for (const given of givenList) {
        const { i_insIdentityResult, Identity } = await getInsFromIdentityInformation({
          LpsInfos,
          s_birthName: s_birthName.toUpperCase(),
          s_given: given.toUpperCase(),
          i_sex: patient.userInfo.gender ? patientGenderToInsSex(patient.userInfo.gender) : undefined,
          s_birthDate: dayjs(patient.userInfo.birthday).format('YYYY-MM-DD'),
          s_birthPlace: patient.userInfo?.birthPlaceCode,
        }).unwrap()
        if (i_insIdentityResult === 1) continue
        dispatch(appendAlert({ area: INFO_PATIENT_PAGE_ALERTS, alert: { message: getInsIdentityResultMessage(i_insIdentityResult), type: 'info' } }))
        if (Identity) {
          await updatePatient({
            id: patient._id,
            userInfo: { ...patient.userInfo, birthSurname: Identity.s_birthName, birthName: Identity.s_birthGiven, birthPlaceCode: Identity.s_birthPlace },
            insNIR: Identity.Ins.s_value + Identity.Ins.s_key,
            insOID: Identity.Ins.s_oid,
            insHistory: Identity.History?.map((history) => ({
              value: history.s_value,
              key: history.s_key,
              oid: history.s_oid,
              startDate: history.s_startDate,
              endDate: history.s_endDate,
            })),
          })
          if (Identity.Ins.s_oid === INS_OID['INS-NIA']) {
            setFormErrors({ insNIR: { value: { message: t`Identifiant temporaire`, type: 'validate' } } })
          }
          enqueueSnackbar(t`The patient information has been updated successfully.`, { variant: 'success' })
        }
        return
      }
      // Show No identity found if the program reaches here
      dispatch(appendAlert({ area: INFO_PATIENT_PAGE_ALERTS, alert: { message: getInsIdentityResultMessage(1), type: 'error' } }))
    } catch (error: any) {
      if ('error' in error && typeof error.error === 'string') {
        dispatch(
          appendAlert({
            area: INFO_PATIENT_PAGE_ALERTS,
            alert: { message: error.error, type: 'error' },
          })
        )
      }
    }
  }

  return { searchIns, isFetchingIns }
}
